import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

/**
 * Bloc « Deux maisons, une histoire de famille » / "Two cottages, one family story".
 * Placé après les cartes des deux gîtes, avant l'offre « jusqu'à 10 personnes ».
 * La photo de Georges Yvois et Marguerite Bouc est une photographie de famille fournie.
 */
export function FamilyStory({ locale = "fr" }) {
  const en = locale === "en";
  return (
    <motion.section
      data-testid={`family-story-${locale}`}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      variants={fadeUp}
      className="bg-cream"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <div className="grid md:grid-cols-[340px_1fr] lg:grid-cols-[420px_1fr] gap-10 lg:gap-14 items-center">
          <motion.figure
            initial={{ opacity: 0, y: 24, rotate: 0 }}
            whileInView={{ opacity: 1, y: 0, rotate: -1.15 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="bg-[#f0e6d4] p-3 rounded-xl shadow-md"
          >
            <img
              src="/images/famille/georges-yvois-marguerite-bouc.jpg"
              alt={en ? "Georges Yvois and Marguerite Bouc, Anne's parents" : "Georges Yvois et Marguerite Bouc, les parents d'Anne"}
              className="w-full aspect-[1440/1053] object-cover rounded-lg"
            />
            <figcaption className="text-center font-serif text-sm text-charcoal/70 pt-3 pb-1">
              {en ? "Georges Yvois and Marguerite Bouc · family photograph" : "Georges Yvois et Marguerite Bouc · photographie de famille"}
            </figcaption>
          </motion.figure>

          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.28em] text-terracotta">
              {en ? "A portrait by Ben" : "Un portrait signé Ben"}
            </span>
            <h2 className="font-serif text-3xl lg:text-4xl xl:text-5xl font-light text-forest mt-4 leading-tight">
              {en ? "A Glass of Chablis with Anne" : "Un verre de chablis chez Anne"}
            </h2>
            <p className="text-charcoal/70 mt-6 text-lg leading-relaxed max-w-2xl">
              {en
                ? "It began with a glass of Chablis by the fire, in one of the cottages Anne's father built. From Georges the mason to Marguerite's research and the fate of Henriette Virginie, Anne's family story is one to be heard from Anne herself."
                : "Tout a commencé par un verre de chablis, au coin du feu, dans l'un des gîtes bâtis par le père d'Anne. De Georges le maçon aux recherches de Marguerite et au destin d'Henriette Virginie, l'histoire de la famille d'Anne s'écoute de sa voix."}
            </p>
            <Link
              to={en ? "/en/yvois-family-story" : "/histoire-famille-yvois"}
              data-testid={`family-story-cta-${locale}`}
              className="inline-flex items-center gap-2 mt-7 text-forest font-medium hover:gap-3 transition-all"
            >
              {en ? "Read the portrait" : "Lire le portrait"}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
